/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { pathnameHasLocale, stripLocalePrefix } from "@cosmos/common-utils";
import { setCookieLocaleHandler } from "@wf-components/navigation-consumer";

import type { LocalePrefixMode } from "../types/locale.types";
import type { NavLocaleClickEvent } from "@wf-components/navigation-consumer";

export const localePrefixModes: LocalePrefixMode[] = ["always", "as-needed", "never"] as const;

/**
 * Returns the locale prefix mode configured via the `NEXT_PUBLIC_LOCALE_MODE`
 * environment variable, falling back to `"always"` if not set.
 *
 * @returns The active {@link LocalePrefixMode} — one of `"always"`, `"as-needed"`, or `"never"`
 *
 * @example
 * // NEXT_PUBLIC_LOCALE_MODE=as-needed
 * getLocalePrefixMode() // => "as-needed"
 *
 * @example
 * // NEXT_PUBLIC_LOCALE_MODE unset
 * getLocalePrefixMode() // => "always"
 */
export function getLocalePrefixMode(): LocalePrefixMode {
	const localePrefixMode = process.env.NEXT_PUBLIC_LOCALE_MODE as LocalePrefixMode | undefined;
	if (!localePrefixMode) {
		return "always";
	}
	if (!localePrefixModes.includes(localePrefixMode)) {
		// We should never see this message, but just in case we throw warning.
		console.warn(`Invalid NEXT_PUBLIC_LOCALE_MODE: "${localePrefixMode}". Falling back to "always".`);
		return "always";
	}
	return localePrefixMode;
}

/**
 * Returns true when locale is expressed via query parameter (?lang=) instead of URL path prefix.
 * Uses an explicit locale prefix mode when supplied, otherwise falls back to
 * NEXT_PUBLIC_LOCALE_MODE.
 */
export function isQueryParamLocaleMode(localePrefix?: LocalePrefixMode): boolean {
	return (localePrefix ?? getLocalePrefixMode()) === "never";
}

/**
 * Normalizes a path to ensure it starts with a forward slash
 *
 * @param path - The path to normalize
 * @returns The path with a leading slash
 *
 * @example
 * normalizePath("about") // => "/about"
 * normalizePath("/about") // => "/about"
 */
export function normalizePath(path: string): string {
	return path.startsWith("/") ? path : `/${path}`;
}

/**
 * Generates a locale-aware path based on prefix mode
 *
 * @param path - The path without locale prefix (e.g., "/about")
 * @param locale - Current locale (e.g., "de")
 * @param defaultLocale - Default locale (e.g., "en-US")
 * @param localePrefix - Prefix mode configuration (defaults to "always")
 * @returns The path with or without locale prefix based on configuration
 *
 * @example
 * // With "always" mode (default)
 * getLocalizedPath("/about", "de", "en-US", "always") // => "/de/about"
 * getLocalizedPath("/about", "en-US", "en-US", "always") // => "/en-US/about"
 *
 * @example
 * // With "as-needed" mode
 * getLocalizedPath("/about", "de", "en-US", "as-needed") // => "/de/about"
 * getLocalizedPath("/about", "en-US", "en-US", "as-needed") // => "/about"
 *
 * @example
 * // With "never" mode
 * getLocalizedPath("/about", "de", "en-US", "never") // => "/about"
 *
 * @example
 * // In navigation components
 * const GlobalNav = () => {
 *   const { getPath } = useLocalePrefix();
 *   return <Link href={getPath("/about", locale)}>About</Link>;
 * };
 *
 * @example
 * // In sitemap generation
 * const sitemapUrl = getLocalizedPath(slug, locale, defaultLocale, "as-needed");
 */
export function getLocalizedPath(
	path: string,
	locale: string,
	defaultLocale: string,
	localePrefix: LocalePrefixMode = "always",
): string {
	const normalizedPath = normalizePath(path);

	if (localePrefix === "never") {
		return normalizedPath;
	}

	if (localePrefix === "as-needed" && locale === defaultLocale) {
		return normalizedPath;
	}

	return `/${locale}${normalizedPath}`;
}

/**
 * Removes locale prefix from path if present
 *
 * @param path - The path that may contain a locale prefix
 * @param locales - Array of supported locale codes
 * @returns The path without locale prefix
 *
 * @example
 * removeLocaleFromPath("/en-US/about", ["en-US", "de"]) // => "/about"
 * removeLocaleFromPath("/about", ["en-US", "de"]) // => "/about"
 */
export function removeLocaleFromPath(path: string, locales: string[]): string {
	if (!path || !locales?.length) return path || "/";

	const normalizedPath = normalizePath(path);

	for (const locale of locales) {
		if (normalizedPath.startsWith(`/${locale}/`)) {
			return normalizedPath.slice(locale.length + 1);
		}
		if (normalizedPath === `/${locale}`) {
			return "/";
		}
	}

	return normalizedPath;
}

/**
 * Checks if the given locale should have a URL prefix based on the prefix mode
 *
 * @param locale - The locale to check
 * @param defaultLocale - The default locale
 * @param localePrefix - The prefix mode
 * @returns Whether the locale should have a URL prefix
 */
export function shouldHaveLocalePrefix(
	locale: string,
	defaultLocale: string,
	localePrefix: LocalePrefixMode = "always",
): boolean {
	if (localePrefix === "never") {
		return false;
	}

	if (localePrefix === "as-needed") {
		return locale !== defaultLocale;
	}

	return true;
}

/**
 * Checks if Next.js is running in development mode.
 *
 * @returns true if NODE_ENV is "development"
 */
export function isDevMode(): boolean {
	return process.env.NODE_ENV === "development";
}

/**
 * Returns a locale-aware navigation path based on the active locale prefix mode
 * (`NEXT_PUBLIC_LOCALE_MODE`). Delegates to {@link getLocalizedPath} so all three
 * modes ("always", "as-needed", "never") are handled consistently:
 *
 * - **"always"** — every locale gets a prefix: `/de/about`, `/en-US/about`
 * - **"as-needed"** — default locale has no prefix (`/about`), others do (`/de/about`)
 * - **"never"** — no prefix for any locale; middleware resolves via cookies/headers
 *
 * If the path already contains a recognised locale prefix it is returned as-is to
 * avoid double-prefixing.
 *
 * @param path - The relative path (e.g., "/about")
 * @param locale - The current locale (e.g., "de")
 * @param locales - Array of supported locale codes
 * @param defaultLocale - The default locale (e.g., "en-US"), used for "as-needed" mode
 * @returns The path with or without locale prefix based on the active locale mode
 *
 * @example
 * // NEXT_PUBLIC_LOCALE_MODE=never
 * getNavLocalePath("/about", "de", locales, "en-US") // => "/about"
 *
 * @example
 * // NEXT_PUBLIC_LOCALE_MODE=always
 * getNavLocalePath("/about", "de", locales, "en-US") // => "/de/about"
 *
 * @example
 * // NEXT_PUBLIC_LOCALE_MODE=as-needed
 * getNavLocalePath("/about", "en-US", locales, "en-US") // => "/about"
 * getNavLocalePath("/about", "de", locales, "en-US")    // => "/de/about"
 */
export function getNavLocalePath(
	path: string,
	locale: string,
	locales: string[],
	defaultLocale: string,
	localePrefix?: LocalePrefixMode,
): string {
	const normalizedPath = normalizePath(path);
	const mode = localePrefix ?? getLocalePrefixMode();

	// In "never" mode, strip any locale prefix — URLs must be clean.
	if (mode === "never") {
		return stripLocalePrefix(locales, normalizedPath);
	}

	if (pathnameHasLocale(locales, normalizedPath)) {
		return normalizedPath;
	}

	return getLocalizedPath(normalizedPath, locale, defaultLocale, mode);
}

/**
 * Locale click handler for development mode.
 * Sets cookieSecure to false to allow cookies on http://localhost.epicgames-gamedev.ol.epicgames.net:3000.
 */
export const setCookieLocaleHandlerDev = (e: Event) => setCookieLocaleHandler(e, { cookieSecure: false });

/**
 * Extracts the target locale code from an `epic-nav-locale-click` event, preferring the
 * site-mapped code (`mappedLangCode`) over the raw nav code (`langCode`) because it
 * matches the vocabulary of the locales the site renders with. Shared so consumers
 * with a custom locale handler resolve the code one way instead of re-deriving it.
 */
export const getLocaleFromClickEvent = (e: Event): string | undefined => {
	const { detail } = e as CustomEvent<NavLocaleClickEvent>;
	return detail?.link?.mappedLangCode || detail?.link?.langCode || undefined;
};

/**
 * Returns the appropriate locale onClick handler based on locale mode.
 * In query param mode, returns setCookieLocaleHandler (dev version in dev mode).
 *
 * @returns The locale click handler function, or undefined if not in query param mode
 *
 * @example
 * const localeOnClick = getLocaleOnClick();
 * const GlobalNav = () => {
 *   return <EpicNavigation localeOnClick={localeOnClick} />;
 * };
 */
export const getLocaleOnClick = (localePrefix?: LocalePrefixMode): undefined | ((e: Event) => Promise<void>) => {
	if (!isQueryParamLocaleMode(localePrefix)) {
		return undefined;
	}

	if (isDevMode()) return setCookieLocaleHandlerDev;

	return setCookieLocaleHandler;
};
